import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import ContactFormModal from '../components/ContactFormModal';
import { PageSEO } from '../components/SEO';
import { homeJsonLd } from '../lib/structuredData';

const Home = () => {
  const [chartAnimated, setChartAnimated] = useState(false);
  const [videoCardsAnimated, setVideoCardsAnimated] = useState(false);
  const [heroCoastersReady, setHeroCoastersReady] = useState(false);
  const [heroFloating, setHeroFloating] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const barChartRef = useRef(null);
  const videoShowcaseRef = useRef(null);

  // Randomized coaster positions
  const [coasterConfig, setCoasterConfig] = useState([
    { top: '0%', left: '0%', rotation: -15, zIndex: 10, delay: 0.2 },
    { top: '5%', left: '35%', rotation: 10, zIndex: 12, delay: 0.5 },
    { top: '40%', left: '15%', rotation: -5, zIndex: 11, delay: 0.8 }
  ]);

  // Randomize coasters on mount
  useEffect(() => {
    const positions = [
      { top: '0%', left: '0%' },
      { top: '5%', left: '35%' },
      { top: '40%', left: '15%' }
    ];

    // Shuffle positions
    for (let i = positions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [positions[i], positions[j]] = [positions[j], positions[i]];
    }

    // Random rotations
    const rotations = [
      Math.floor(Math.random() * 40) - 20,
      Math.floor(Math.random() * 40) - 20,
      Math.floor(Math.random() * 40) - 20
    ];

    // Shuffle z-indexes
    const zIndexes = [10, 11, 12];
    for (let i = zIndexes.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [zIndexes[i], zIndexes[j]] = [zIndexes[j], zIndexes[i]];
    }

    // Shuffle delays
    const delays = [0.2, 0.5, 0.8];
    for (let i = delays.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [delays[i], delays[j]] = [delays[j], delays[i]];
    }

    setCoasterConfig([
      { ...positions[0], rotation: rotations[0], zIndex: zIndexes[0], delay: delays[0] },
      { ...positions[1], rotation: rotations[1], zIndex: zIndexes[1], delay: delays[1] },
      { ...positions[2], rotation: rotations[2], zIndex: zIndexes[2], delay: delays[2] }
    ]);

    // Trigger entrance animation
    setTimeout(() => setHeroCoastersReady(true), 100);

    // Switch to floating after entrance
    setTimeout(() => setHeroFloating(true), 2000);
  }, []);

  // Scroll animations
  useEffect(() => {
    const handleScroll = () => {
      // Reveal animations
      document.querySelectorAll('.reveal').forEach(el => {
        const rect = el.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          el.classList.add('active');
        }
      });

      // Bar chart animation
      if (barChartRef.current && !chartAnimated) {
        const rect = barChartRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          setChartAnimated(true);
        }
      }

      // Video cards animation
      if (videoShowcaseRef.current && !videoCardsAnimated) {
        const rect = videoShowcaseRef.current.getBoundingClientRect();
        if (rect.top < window.innerHeight - 100) {
          setVideoCardsAnimated(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, [chartAnimated, videoCardsAnimated]);

  const coasterImages = [
    'https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-8a-community.png',
    'https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-1c-value.png',
    'https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-3b-context.png'
  ];

  return (
    <>
      <PageSEO.Home jsonLd={homeJsonLd()} />
      <div className="bg-[#FEF7ED] text-[#1e1b4b]">
        <style>{`
          .font-display { font-family: 'Faustina', Georgia, serif; }
          .font-body { font-family: 'DM Sans', sans-serif; }
          .font-mono { font-family: 'JetBrains Mono', monospace; }

          .gradient-text {
            background: linear-gradient(135deg, #ea580c 0%, #f97316 50%, #ea580c 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
          }

          @keyframes hero-drop {
          0% { opacity: 0; transform: var(--start-transform); }
          100% { opacity: 1; transform: var(--end-transform); }
        }

        @keyframes hero-float-1 {
          0%, 100% { transform: var(--float-base) translateY(0); }
          50% { transform: var(--float-base) translateY(-14px); }
        }
        @keyframes hero-float-2 {
          0%, 100% { transform: var(--float-base) translateY(0); }
          50% { transform: var(--float-base) translateY(-12px); }
        }
        @keyframes hero-float-3 {
          0%, 100% { transform: var(--float-base) translateY(0); }
          50% { transform: var(--float-base) translateY(-10px); }
        }

        .hero-coaster-animate {
          animation: hero-drop 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
        }

        .hero-float-1 { animation: hero-float-1 4s ease-in-out infinite; }
        .hero-float-2 { animation: hero-float-2 4.5s ease-in-out infinite; }
        .hero-float-3 { animation: hero-float-3 5s ease-in-out infinite; }

        @keyframes badge-pop {
          0% { opacity: 0; transform: scale(0.5); }
          50% { transform: scale(1.1); }
          100% { opacity: 1; transform: scale(1); }
        }
        .badge-pop { animation: badge-pop 0.5s cubic-bezier(0.16, 1, 0.3, 1) 1s forwards; opacity: 0; }

        @keyframes pulse-glow {
          0%, 100% { filter: drop-shadow(0 0 20px rgba(249, 115, 22, 0.3)); }
          50% { filter: drop-shadow(0 0 35px rgba(249, 115, 22, 0.5)); }
        }
        .pulse-glow { animation: pulse-glow 3s ease-in-out infinite; }

        .reveal {
          opacity: 0;
          transform: translateY(40px);
          transition: all 0.8s cubic-bezier(0.16, 1, 0.3, 1);
        }
        .reveal.active {
          opacity: 1;
          transform: translateY(0);
        }

        .hover-lift {
          transition: transform 0.4s ease, box-shadow 0.4s ease;
        }
        .hover-lift:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 60px rgba(234, 88, 12, 0.15);
        }

        .bar-animate {
          transition: width 1.5s cubic-bezier(0.16, 1, 0.3, 1);
        }

        @keyframes card-float-1 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes card-float-2 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        @keyframes card-float-3 {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .video-card-float-1 { animation: card-float-1 4s ease-in-out infinite; }
        .video-card-float-2 { animation: card-float-2 5s ease-in-out infinite; }
        .video-card-float-3 { animation: card-float-3 4.5s ease-in-out infinite; }

        details summary::-webkit-details-marker { display: none; }
        details summary { list-style: none; }
      `}</style>

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-36 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          {/* Background Video */}
          {/* TODO: Upload to DO Spaces and update URL to: https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/videos/hero-background.mp4 */}
          <div className="absolute inset-0">
            <video
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            >
              <source src="https://iolab.co/wp-content/uploads/2026/01/0e19e5c1-7b11-4025-ace3-55cb2ebf7a3b.mp4" type="video/mp4" />
            </video>
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-[#FEF7ED]/80 via-[#FEF7ED]/60 to-[#FEF7ED]/80"></div>
          <div className="absolute top-20 left-10 w-96 h-96 bg-orange-200/40 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-amber-200/40 rounded-full blur-3xl"></div>
        </div>

        {/* V5 Gradient Line at Bottom */}
        <div className="absolute bottom-0 left-0 w-full h-1 z-20" style={{ background: 'linear-gradient(to right, #8b5cf6 0%, #f97316 100%)' }}></div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Copy */}
            <motion.div
              className="space-y-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* V5 Code Accent */}
              <p className="font-mono text-xs text-[#1e1b4b]/30">// welcome_to_tappd</p>

              <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full shadow-lg">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-sm font-semibold text-white font-body">Tappd Is Now Live</span>
              </div>

              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#1e1b4b] leading-tight" style={{ fontWeight: 500 }}>
                Get more <span className="gradient-text">local customers</span> from bar coasters
              </h1>

              <p className="text-xl text-[#1e1b4b]/60 leading-relaxed font-body">
                Your deal. On every table. At every bar in town. Tappd puts your business
                in front of customers at the perfect moment — when they're out and deciding where to go next.{' '}
                <a href="#how-it-works" className="font-semibold text-[#1e1b4b] underline underline-offset-2 whitespace-nowrap">
                  See how it works
                </a>
              </p>

              {/* Stats row */}
              <div className="flex flex-wrap gap-8">
              </div>

              {/* CTAs — advertiser first, then the patron path */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/pricing" className="inline-flex items-center justify-center gap-2 px-8 py-4 min-h-[44px] bg-gradient-to-r from-orange-500 to-orange-600 rounded-full text-lg font-semibold text-white shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all">
                  See Plans
                  <ArrowRight className="w-5 h-5" aria-hidden="true" />
                </Link>
                <Link to="/local-deals-near-me" className="inline-flex items-center justify-center gap-2 px-8 py-4 min-h-[44px] border-2 border-[#1e1b4b]/20 rounded-full text-lg font-semibold text-[#1e1b4b] hover:bg-white/50 transition-all">
                  Find deals near me
                </Link>
              </div>

              {/* One line for the patron who just scanned a coaster. */}
              <p className="text-base text-[#1e1b4b]/60 font-body">
                Scanned a coaster?{' '}
                <Link to="/scan" className="font-semibold text-orange-600 underline underline-offset-2 hover:text-orange-700">
                  Tap it to see the deals at your table
                </Link>
                .
              </p>
            </motion.div>

            {/* Coaster Visual */}
            <motion.div
              className="relative flex justify-center lg:justify-end"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative w-[280px] h-[280px] sm:w-[380px] sm:h-[380px] md:w-[480px] md:h-[480px] lg:w-[540px] lg:h-[540px]">
                {coasterConfig.map((config, index) => {
                  const isMiddle = index === 1;
                  const sizeClass = index === 2
                    ? 'w-56 h-56 md:w-64 md:h-64 lg:w-72 lg:h-72'
                    : 'w-60 h-60 md:w-72 md:h-72 lg:w-80 lg:h-80';

                  return (
                    <div
                      key={index}
                      className={`absolute ${sizeClass} ${isMiddle ? 'pulse-glow' : ''} ${heroFloating ? `hero-float-${index + 1}` : ''}`}
                      style={{
                        top: config.top,
                        left: config.left,
                        zIndex: config.zIndex,
                        opacity: heroCoastersReady ? 1 : 0,
                        transform: heroFloating
                          ? `rotate(${config.rotation}deg)`
                          : `rotate(${config.rotation}deg) translateY(${heroCoastersReady ? 0 : 80}px) scale(${heroCoastersReady ? 1 : 0.8})`,
                        transition: heroFloating ? 'none' : `all 1.2s cubic-bezier(0.16, 1, 0.3, 1) ${config.delay}s`,
                        '--float-base': `rotate(${config.rotation}deg)`
                      }}
                    >
                      <img
                        src={coasterImages[index]}
                        alt="Tappd Coaster"
                        className="w-full h-full object-contain drop-shadow-2xl"
                      />
                    </div>
                  );
                })}

                {/* Scan badge */}
                <div className="absolute -bottom-2 left-4 bg-white rounded-xl px-4 py-3 shadow-xl border border-orange-100 z-20 badge-pop">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-bold text-[#1e1b4b] font-body">Scan to claim</div>
                      <div className="text-xs text-[#1e1b4b]/50 font-body">No app needed</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* THE PROBLEM */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 rounded-full text-red-600 text-sm font-medium mb-6 font-body">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
              The Problem
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              Traditional ads aren't working anymore
            </h2>
            <p className="text-lg text-[#1e1b4b]/60 max-w-2xl mx-auto font-body">
              You're spending money on ads that people scroll past, skip, or ignore entirely.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-16 reveal">
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 text-center">
              <div className="text-4xl mb-3">📱</div>
              <div className="text-2xl font-bold text-[#1e1b4b] mb-1">0.9%</div>
              <div className="text-[#1e1b4b]/50 text-sm font-body">Facebook Ads CTR</div>
              <div className="text-red-500 text-xs mt-2 font-body">Scrolled past in 0.3 seconds</div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 text-center">
              <div className="text-4xl mb-3">🔍</div>
              <div className="text-2xl font-bold text-[#1e1b4b] mb-1">3.2%</div>
              <div className="text-[#1e1b4b]/50 text-sm font-body">Google Ads CTR</div>
              <div className="text-red-500 text-xs mt-2 font-body">Competing with 10+ other ads</div>
            </div>
            <div className="bg-gray-50 rounded-2xl p-6 border border-gray-200 text-center">
              <div className="text-4xl mb-3">📬</div>
              <div className="text-2xl font-bold text-[#1e1b4b] mb-1">1.0%</div>
              <div className="text-[#1e1b4b]/50 text-sm font-body">Direct Mail Response</div>
              <div className="text-red-500 text-xs mt-2 font-body">Straight to the trash</div>
            </div>
          </div>

          <div className="text-center reveal">
            <p className="text-xl text-[#1e1b4b]/70 font-medium font-body">
              The problem isn't your offer. <span className="text-orange-600">It's the timing.</span>
            </p>
          </div>
        </div>
      </section>

      {/* THE SOLUTION */}
      <section className="py-20 bg-gradient-to-b from-[#1e1b4b] to-[#2d2a5e] text-white overflow-hidden">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Story */}
            <div className="reveal">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full text-orange-300 text-sm font-medium mb-6 font-body">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                </svg>
                The Solution
              </div>

              <h2 className="font-display text-3xl md:text-4xl mb-6">
                Reach them at the <span className="text-orange-400">perfect moment</span>
              </h2>

              <div className="bg-white/5 rounded-2xl p-6 border border-white/10 mb-8">
                <div className="flex items-start gap-4">
                  <div className="text-3xl">💬</div>
                  <div>
                    <p className="text-white/90 text-lg italic mb-2 font-body">
                      "What should we do after this?"
                    </p>
                    <p className="text-white/50 text-sm font-body">
                      — Every group of friends at every bar, every night
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-white/70 text-lg leading-relaxed mb-8 font-body">
                When people are out having drinks, they're actively looking for their next move.
                A Tappd coaster puts your business right in front of them — at exactly the right moment.
              </p>

              <div className="space-y-4">
                {[
                  "No scrolling past — it's right under their drink",
                  "Perfect timing — they're already out and about",
                  "Hyperlocal — within 15 miles of your business"
                ].map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-white/80 font-body">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Comparison Chart */}
            <div className="reveal" ref={barChartRef}>
              <div className="bg-white/5 rounded-3xl p-8 border border-white/10">
                <h3 className="text-xl font-semibold mb-8 text-center font-body">Engagement Rate Comparison</h3>

                <div className="space-y-6">
                  <div>
                    <div className="flex justify-between text-sm mb-2 font-body">
                      <span className="text-white/60">Facebook Ads</span>
                      <span className="text-white/60">0.9%</span>
                    </div>
                    <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-white/30 rounded-full bar-animate"
                        style={{ width: chartAnimated ? '7%' : '0%' }}
                      ></div>
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between text-sm mb-2 font-body">
                      <span className="text-white/60">Google Ads</span>
                      <span className="text-white/60">3.2%</span>
                    </div>
                    <div className="h-4 bg-white/10 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-white/30 rounded-full bar-animate"
                        style={{ width: chartAnimated ? '26%' : '0%' }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-[#FEF7ED]" id="how-it-works">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full text-orange-700 text-sm font-medium mb-6 font-body">
              Simple Process
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              How Tappd works
            </h2>
            <p className="text-lg text-[#1e1b4b]/60 font-body">Three simple steps to start getting customers</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 reveal">
            {[
              {
                num: '1',
                icon: (
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                  </svg>
                ),
                title: 'Create Your Offer',
                desc: 'Set up your deal in minutes. "10% off", "Free appetizer", "BOGO" — whatever works for your business.'
              },
              {
                num: '2',
                icon: (
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                ),
                title: 'We Place Coasters',
                desc: 'We distribute Tappd coasters to bars, breweries, and pubs within 15 miles of your business.'
              },
              {
                num: '3',
                icon: (
                  <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                  </svg>
                ),
                title: 'Watch Them Come In',
                desc: "Track every scan, claim, and redemption in real-time. See exactly how many customers you're getting."
              }
            ].map((step, idx) => (
              <div key={idx} className="hover-lift bg-white rounded-2xl p-8 shadow-lg border border-orange-100 relative">
                <div className="absolute -top-4 -left-4 w-12 h-12 bg-gradient-to-br from-orange-500 to-amber-500 rounded-xl flex items-center justify-center text-white font-display text-xl shadow-lg">{step.num}</div>
                <div className="w-16 h-16 bg-orange-100 rounded-2xl flex items-center justify-center mb-6">
                  {step.icon}
                </div>
                <h3 className="font-display text-xl text-[#1e1b4b] mb-3">{step.title}</h3>
                <p className="text-[#1e1b4b]/60 font-body">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VIDEO SHOWCASE */}
      <section className="py-24 bg-white overflow-hidden" id="coasters">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Video Mockup */}
            <div className="relative flex justify-center order-2 lg:order-1" ref={videoShowcaseRef}>
              <div className="relative w-full max-w-[520px]">
                <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                  <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="w-full h-auto aspect-video object-cover"
                  >
                    <source src="https://customer-assets.emergentagent.com/job_e6024afa-0232-4acc-8b70-db36c46216a9/artifacts/agz7cwuk_tappd-hp-vid.mp4" type="video/mp4" />
                  </video>
                </div>

                {/* Deal Card */}
                <div className={`absolute -top-6 -right-4 md:-top-8 md:-right-6 bg-white rounded-2xl p-3 shadow-xl border border-gray-100 w-[180px] transition-all duration-700 ${videoCardsAnimated ? 'opacity-100 translate-y-0 video-card-float-1' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.2s' }}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                      <div className="w-5 h-5 bg-purple-400 rounded-full"></div>
                    </div>
                    <div>
                      <div className="font-semibold text-[#1e1b4b] text-xs font-body">Sunflower Cafe</div>
                      <div className="text-[10px] text-[#1e1b4b]/50 font-body">0.3 miles away</div>
                    </div>
                  </div>
                  <div className="text-orange-500 font-semibold text-xs mb-1 font-body">BOGO Coffee · Claimed!</div>
                  <div className="flex items-center gap-1">
                    <div className="flex text-amber-400">
                      {[...Array(5)].map((_, i) => (
                        <svg key={i} className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                      ))}
                    </div>
                    <span className="text-[10px] text-[#1e1b4b]/50 font-body">47 visits</span>
                  </div>
                </div>

                {/* Scan Prompt Card */}
                <div className={`absolute -bottom-16 -left-4 md:-bottom-8 md:-left-6 bg-white rounded-2xl p-3 shadow-xl border border-gray-100 transition-all duration-700 z-10 ${videoCardsAnimated ? 'opacity-100 translate-y-0 video-card-float-2' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.4s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center">
                      <span className="text-lg">🍺</span>
                    </div>
                    <div>
                      <div className="text-[10px] text-[#1e1b4b]/50 font-body">Scan a coaster at</div>
                      <div className="font-semibold text-[#1e1b4b] text-sm font-body">Ott's Tavern</div>
                      <div className="text-orange-500 text-xs font-medium font-body">See the deal →</div>
                    </div>
                  </div>
                </div>

                {/* Stats Badge */}
                <div className={`absolute bottom-8 -right-2 md:-bottom-6 md:right-12 bg-[#1e1b4b] rounded-xl px-4 py-2 shadow-xl transition-all duration-700 z-20 ${videoCardsAnimated ? 'opacity-100 translate-y-0 video-card-float-3' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '0.6s' }}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-[#2d2a5e] rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                      </svg>
                    </div>
                    <div>
                      <div className="text-[10px] text-white/60 font-body">This week</div>
                      <div className="text-lg font-bold text-emerald-400">+23 customers</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Copy */}
            <div className="space-y-8 order-1 lg:order-2 reveal">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100 rounded-full">
                <span className="text-sm font-semibold text-orange-700 font-body">See It In Action</span>
              </div>

              <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] leading-tight">
                Your deal on <span className="gradient-text">every table</span>
              </h2>

              <p className="text-xl text-[#1e1b4b]/60 leading-relaxed font-body">
                Watch how it works: customers scan a Tappd coaster, discover your deal nearby,
                and head straight to your business. Real customers. Real visits. Tracked in real-time.
              </p>

              <div className="space-y-4">
                {[
                  { title: 'Instant discovery', desc: 'Customers find your deal the moment they scan — no app download needed.' },
                  { title: 'Proximity-based', desc: 'Your offer shows to customers within 15 miles — already out and ready to visit.' },
                  { title: 'Track every visit', desc: 'See exactly how many customers came from Tappd in your real-time dashboard.' }
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Check className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-[#1e1b4b] mb-1 font-body">{item.title}</h4>
                      <p className="text-[#1e1b4b]/60 text-sm font-body">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* BUILT FOR */}
      <section className="py-20 bg-[#FEF7ED]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              Built for local businesses like yours
            </h2>
            <p className="text-lg text-[#1e1b4b]/60 font-body">If you serve the local community, Tappd works for you</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 reveal">
            {[
              { emoji: '💇', name: 'Salons & Spas' },
              { emoji: '🚗', name: 'Auto Services' },
              { emoji: '🏋️', name: 'Fitness Studios' },
              { emoji: '🎯', name: 'Entertainment' },
              { emoji: '🛍️', name: 'Retail Shops' },
              { emoji: '🔧', name: 'Home Services' },
              { emoji: '🐕', name: 'Pet Services' },
              { emoji: '💼', name: 'Professional' }
            ].map((type, idx) => (
              <div key={idx} className="hover-lift bg-white rounded-2xl p-6 text-center border border-orange-100 shadow-sm">
                <div className="text-3xl mb-3">{type.emoji}</div>
                <div className="font-semibold text-[#1e1b4b] font-body">{type.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-gradient-to-b from-[#1e1b4b] to-[#2d2a5e] text-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16 reveal">
            <h2 className="font-display text-3xl md:text-4xl mb-4">
              Local businesses love <span className="text-orange-400">the results</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 reveal">
            {[
              { quote: 'We tried Facebook ads, Google ads, mailers — nothing worked. Tappd brought us <span class="text-orange-400 font-semibold">52 new customers</span> in the first month.', name: 'Mike R.', business: "Angelo's Pizza, Marlton", emoji: '🍕' },
              { quote: 'The dashboard shows exactly where customers are coming from. It\'s like having a marketing team for <span class="text-orange-400 font-semibold">$188/month</span>.', name: 'Sarah T.', business: 'Style Studio, Cherry Hill', emoji: '💇' },
              { quote: 'Way more effective than our Instagram ads. These aren\'t just clicks — they\'re <span class="text-orange-400 font-semibold">people walking through our door</span>.', name: 'Tom D.', business: 'SJ Auto Spa, Medford', emoji: '🚗' }
            ].map((testimonial, idx) => (
              <div key={idx} className="bg-white/5 backdrop-blur rounded-2xl p-8 border border-white/10">
                <div className="flex items-center gap-1 text-orange-400 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                  ))}
                </div>
                <p className="text-white/80 mb-6 leading-relaxed font-body" dangerouslySetInnerHTML={{ __html: `"${testimonial.quote}"` }}></p>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-full flex items-center justify-center text-xl">{testimonial.emoji}</div>
                  <div>
                    <div className="font-semibold text-white font-body">{testimonial.name}</div>
                    <div className="text-sm text-white/50 font-body">{testimonial.business}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VENUE PARTNER CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#1e1b4b] to-[#2d2a5e] rounded-3xl p-8 md:p-12 overflow-hidden relative reveal">
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500 rounded-full blur-3xl"></div>
              <div className="absolute bottom-0 left-0 w-48 h-48 bg-amber-500 rounded-full blur-3xl"></div>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center relative z-10">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 rounded-full text-green-300 text-sm font-medium mb-4 font-body">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                  100% Free
                </div>
                <h3 className="font-display text-3xl md:text-4xl text-white mb-4">
                  Own a bar or restaurant?
                </h3>
                <p className="text-white/70 text-lg mb-6 font-body">
                  Get 5,000 premium coasters delivered free. Your customers discover local deals.
                  We never feature competing F&B businesses.
                </p>
                <Link to="/for-venues" className="inline-flex items-center gap-2 px-8 py-4 bg-white rounded-full text-[#1e1b4b] font-semibold hover:bg-orange-50 transition-all">
                  Get Free Coasters
                  <ArrowRight className="w-5 h-5" />
                </Link>
              </div>

              <div className="flex justify-center">
                <div className="relative">
                  <img src="https://tappd-uploads.nyc3.cdn.digitaloceanspaces.com/coaster_designs/tappd-coaster-8c-community.png" alt="Free Tappd Coasters" className="w-48 h-48 md:w-56 md:h-56 object-contain drop-shadow-2xl"/>
                  <div className="absolute -top-2 -right-2 bg-gradient-to-r from-orange-500 to-amber-500 rounded-xl px-3 py-2 shadow-lg">
                    <div className="text-white font-bold text-lg">5,000</div>
                    <div className="text-white/80 text-xs font-body">FREE</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#FEF7ED]">
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12 reveal">
            <h2 className="font-display text-3xl md:text-4xl text-[#1e1b4b] mb-4">
              Frequently asked questions
            </h2>
          </div>

          <div className="space-y-4 reveal">
            {[
              { q: 'How quickly will I see results?', a: 'Most businesses see their first customers within 1-2 weeks. Your deal goes live at the venues you pick as soon as your subscription is active and our team has reviewed your listing.' },
              { q: 'What types of businesses work best?', a: "Any local service business does well: salons, auto shops, fitness studios, retail, entertainment, professional services, and more. We don't feature restaurants or bars (they're our venue partners!)." },
              { q: 'Can I change or update my offer?', a: 'Yes! You can update your deal anytime through your dashboard. Changes go live immediately across all coasters.' },
              { q: 'Is there a contract or commitment?', a: 'No contracts, ever. Monthly plans renew month to month; annual plans are billed yearly with two months free. Change plan or cancel anytime from the billing portal.' }
            ].map((faq, idx) => (
              <details key={idx} className="group bg-white rounded-2xl border border-orange-100 overflow-hidden">
                <summary className="flex items-center justify-between p-6 cursor-pointer">
                  <span className="font-semibold text-lg text-[#1e1b4b] font-body">{faq.q}</span>
                  <ChevronDown className="w-5 h-5 text-orange-500 transition-transform group-open:rotate-180" />
                </summary>
                <div className="px-6 pb-6 text-[#1e1b4b]/70 font-body">{faq.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-gradient-to-br from-[#1e1b4b] to-[#2d2a5e] text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=1920" alt="" className="w-full h-full object-cover opacity-20"/>
        </div>

        <div className="max-w-4xl mx-auto px-6 text-center relative z-10 reveal">
          <h2 className="font-display text-4xl md:text-5xl mb-6">
            Ready to get more <span className="text-orange-400">local customers?</span>
          </h2>
          <p className="text-xl text-white/60 max-w-2xl mx-auto mb-10 font-body">
            Join Tappd and start reaching customers at the perfect moment.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/pricing"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-gradient-to-r from-orange-500 to-amber-500 rounded-full text-lg font-semibold shadow-xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all"
            >
              See Plans
              <ArrowRight className="w-5 h-5" />
            </Link>
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="inline-flex items-center justify-center gap-2 px-8 py-4 border-2 border-white/30 rounded-full text-lg font-semibold hover:bg-white/10 transition-all"
            >
              Talk to Sales
            </button>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-white/50 text-sm font-body">
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              Plans from $125/mo
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-5 h-5 text-green-400" />
              No contracts
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      <ContactFormModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
      </div>
    </>
  );
};

export default Home;
